import { Session, User } from "@supabase/supabase-js"
import { createContext, ReactNode, useEffect, useState } from "react"
import { useGetUser } from "../api/queries/user"
import { SupabaseAuthContextType } from "../types/auth"
import { createClient } from "../utils/supabase/component"

const AuthContext = createContext<SupabaseAuthContextType | null>(null)
const supabase = createClient()

function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const { data: userData, refetch: refetchUserInfo } = useGetUser(user?.id)

  useEffect(() => {
    const setData = async () => {
      const {
        data: { session },
        error
      } = await supabase.auth.getSession()
      if (error) throw error
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    }

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    setData()

    return () => {
      listener?.subscription.unsubscribe()
    }
  }, [])

  const signInWithEmail = async (email: string, password: string) => {
    setLoading(true)
    const res = await supabase.auth.signInWithPassword({
      email,
      password
    })

    return res
  }

  const signUpWithEmail = async (email: string, password: string) => {
    setLoading(true)
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    })
    setUser(data.user)
    setLoading(false)
    return { user: data.user, error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
  }

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email)
    return { data, error }
  }

  const signInWithGoogle = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=/dashboard`,
        },
      })

      if (error) throw error

      return { data, error: null }
    } catch (error) {
      console.error("Error signing in with Google:", error)
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("Failed to sign in with Google"),
      }
    } finally {
      setLoading(false)
    }
  }

  const contextValue = {
    user,
    session,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword,
    loading,
    refetchUserInfo,
    signInWithGoogle
  }

  return (
    // @ts-ignore
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
